import CircularProgress from '@mui/material/CircularProgress';
import { JsonForms } from '@jsonforms/react';
import {
  duplicateSettings,
  fillPresets,
  getNodeData,
  getNodeSchema,
  getQaNodeData,
  postNodeData,
} from 'api/services/NodeSettings';
import { NodeSettingsContext } from 'providers/NodeSettingsProvider';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Fab from '@mui/material/Fab';
import Button from '@mui/material/Button';
import Build from '@mui/icons-material/Build';
import { generateUiSchema, Schema } from './generateUiSchema';
import style from './Editor.module.scss';
import { materialCells, materialRenderers } from '@jsonforms/material-renderers';
import Ajv, { AnySchema, ErrorObject, ValidateFunction } from 'ajv';
import categorizationRenderer from './CategorizationLayout';
import { UserSettingsRenderer, userSettingsRendererTester } from './renderers';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';

import SchemaDriftPanel from './SchemaDriftPanel';
import SettingsImportDialog from './SettingsImportDialog';
import {
  buildValidationErrorKey,
  extractSchemaDriftIssues,
  formatValidationErrorMessage,
  isSchemaDriftError,
} from './schemaDrift';
import { normaliseResampling } from './resampling';

const renderers = [
  ...materialRenderers,
  categorizationRenderer,
  { tester: userSettingsRendererTester, renderer: UserSettingsRenderer },
];

// Component for a Floating Action Button that expands on hover to show text
const ExpandingFab = styled(Fab)(({ theme }) => ({
  minWidth: '56px',
  width: '56px',
  borderRadius: '28px',
  transition: 'width 0.3s ease-in-out, border-radius 0.3s ease-in-out',
  overflow: 'hidden',
  '& .fab-text': {
    opacity: 0,
    marginLeft: 0,
    width: 0,
    transition: 'opacity 0.2s ease-in-out, margin-left 0.2s ease-in-out, width 0.2s ease-in-out',
    whiteSpace: 'nowrap',
  },
  '&:hover': {
    width: '160px',
    borderRadius: '24px',
    '& .fab-text': {
      opacity: 1,
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
  },
}));

function NodeEditor() {
  const { nodeId, clientId, name, saveSettings } = useContext(NodeSettingsContext);
  const { data, error, isLoading, refetch: refetchSettings } = getNodeData(clientId, nodeId);
  const { data: qaData, error: qaError, isLoading: qaIsLoading } = getQaNodeData(clientId, nodeId);
  const { data: schema } = getNodeSchema(clientId, nodeId);
  const uiSchema = useMemo(() => generateUiSchema(schema as unknown as Schema), [schema]);
  const [formData, setFormData] = useState<any>({});
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const { mutate: updateSettings, isPending: updateInProgress } = postNodeData(clientId ?? '', nodeId ?? '', saveSettings);
  const { mutate: applyPresets, isPending: presetsInProgress } = fillPresets(clientId ?? '', nodeId ?? '', () => {
    // After successful preset application, refetch settings from GET endpoint
    // This uses the same tested code path as page load (F5)
    refetchSettings();
  });
  const { mutate: importSettings, isPending: importInProgress } = duplicateSettings(clientId ?? '', nodeId ?? '', () => {
    setImportDialogOpen(false);
    refetchSettings();
  });
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const displayedErrorKeysRef = useRef<Set<string>>(new Set());

  const ajvWithDefaults = useMemo(() => new Ajv({ useDefaults: true, strict: false }), []);
  const validationAjv = useMemo(() => new Ajv({ allErrors: true, strict: false }), []);
  const schemaValidator = useMemo<ValidateFunction | null>(() => {
    if (!schema) {
      return null;
    }

    return validationAjv.compile(schema as AnySchema);
  }, [schema, validationAjv]);

  const validationErrors = useMemo<ErrorObject[]>(() => {
    if (!schemaValidator || !formData || Object.keys(formData).length === 0) {
      return [];
    }

    schemaValidator(formData);
    return schemaValidator.errors ?? [];
  }, [formData, schemaValidator]);

  const schemaDriftIssues = useMemo(() => extractSchemaDriftIssues(validationErrors), [validationErrors]);
  const nonSchemaDriftErrors = useMemo(
    () => validationErrors.filter((validationError) => !isSchemaDriftError(validationError)),
    [validationErrors],
  );
  const hasValidationErrors = validationErrors.length > 0;

  useEffect(() => {
    if (data && data.has_settings) {
      setFormData(normaliseResampling(data.node_settings));
      return;
    }

    if (schema && data && !data.has_settings) {
      const validate = ajvWithDefaults.compile(schema as AnySchema);
      const defaultData = {
        nodeid: nodeId,
        client_id: parseInt(clientId, 10),
        usersettings: {},
        vendorsettings: {},
      };
      validate(defaultData);
      setFormData(normaliseResampling(defaultData));
      return;
    }

    // Reset formData on node change
    setFormData({});
  }, [ajvWithDefaults, clientId, data, nodeId, schema]);

  const onQaImport = () => {
    if (qaData && qaData.has_settings) {
      setFormData(normaliseResampling(qaData.node_settings));
      enqueueSnackbar('Import to local form has succeeded. To validate changes, CLICK ON SUBMIT!', {
        variant: 'success',
        action: () => null,
      });
      return;
    }

    enqueueSnackbar(`QA import failed. Node has settings in QA? = ${qaData?.has_settings}`, {
      variant: 'error',
      persist: true,
    });
  };

  useEffect(() => {
    const currentErrorKeys = new Set(nonSchemaDriftErrors.map(buildValidationErrorKey));

    nonSchemaDriftErrors.forEach((validationError) => {
      const key = buildValidationErrorKey(validationError);

      if (displayedErrorKeysRef.current.has(key)) {
        return;
      }

      enqueueSnackbar(formatValidationErrorMessage(validationError), {
        preventDuplicate: true,
        key,
        variant: 'error',
        persist: true,
      });
      displayedErrorKeysRef.current.add(key);
    });

    Array.from(displayedErrorKeysRef.current).forEach((key) => {
      if (currentErrorKeys.has(key)) {
        return;
      }

      closeSnackbar(key);
      displayedErrorKeysRef.current.delete(key);
    });
  }, [closeSnackbar, enqueueSnackbar, nonSchemaDriftErrors]);

  useEffect(
    () => () => {
      Array.from(displayedErrorKeysRef.current).forEach((key) => closeSnackbar(key));
      displayedErrorKeysRef.current.clear();
    },
    [closeSnackbar],
  );

  const onSubmit = () => {
    updateSettings(normaliseResampling(formData));
  };

  const onFillPresets = () => {
    applyPresets(normaliseResampling(formData));
  };

  return (
    <div className={style.editor}>
      <div className={style.header}>
        <h1>{name}</h1>
        <Button
          color={'secondary'}
          onClick={onQaImport}
          variant={'outlined'}
          disabled={!!qaError || !!qaIsLoading}
          size={'small'}>
          QA Import
        </Button>
        <Button color={'secondary'} onClick={() => setImportDialogOpen(true)} variant={'outlined'} size={'small'}>
          Import settings from...
        </Button>
      </div>
      <SettingsImportDialog
        isPending={importInProgress}
        onClose={() => setImportDialogOpen(false)}
        onImport={importSettings}
        open={importDialogOpen}
        schema={schema}
      />
      <>
        {isLoading && <CircularProgress />}
        {!isLoading && schema && data && (
          <>
            {!data.has_settings && (
              <div className={style.no_settings_warning}>
                Node has no current settings. Defaults have been injected. Fill and submit the form to create settings.
              </div>
            )}
            <SchemaDriftPanel issues={schemaDriftIssues} />
            <JsonForms
              schema={schema}
              uischema={uiSchema}
              data={formData}
              renderers={renderers}
              cells={materialCells}
              onChange={({ data: updatedData }) => {
                if (updatedData && Object.keys(updatedData).length > 0) {
                  setFormData(normaliseResampling(updatedData));
                }
              }}
              config={{ showUnfocusedDescription: true }}
            />
            <ExpandingFab
              color={'secondary'}
              sx={{
                position: 'fixed',
                bottom: (theme) => theme.spacing(2),
                right: (theme) => theme.spacing(10),
              }}
              disabled={hasValidationErrors || presetsInProgress || updateInProgress}
              onClick={onFillPresets}
              title={'Fill Presets'}>
              <Build />
              <span className={'fab-text'}>Fill Presets</span>
            </ExpandingFab>
            <Fab
              color={'primary'}
              sx={{
                position: 'fixed',
                bottom: (theme) => theme.spacing(2),
                right: (theme) => theme.spacing(2),
              }}
              disabled={hasValidationErrors || updateInProgress}
              onClick={onSubmit}>
              SAVE
            </Fab>
          </>
        )}
        {error && <div>Error getting settings</div>}
      </>
    </div>
  );
}

export default NodeEditor;
